/// <reference types="@angular/localize" />

import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from '@environments/environment';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_ROUTES } from './app/app-routing';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  TitleStrategy,
  withInMemoryScrolling,
} from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { provideAuth } from 'angular-auth-oidc-client';

import { Apollo, provideNamedApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import {
  AuthInterceptor,
  PAGINATION_CONFIG,
  AvatarColorPipe,
  GraphqlInterceptorInterceptor,
  FILTER_COMPONENT_CONFIG,
  SKIP_ERROR_HANDLING_OPERATIONS_LIST,
  defaultMatDialogConfig,
  CustomTitleStrategy,
} from '@tiltgroup/web-components';
import { SqlOperator } from '@graphql-types';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { DecimalPipe } from '@angular/common';
import {
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  ModuleRegistry,
  provideGlobalGridOptions,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  themeQuartz,
} from 'ag-grid-community';
import { agGridThemeParams } from '@shared/ag-grid.theme';

if (environment.production) {
  enableProdMode();
}

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowSelectionModule,
  CsvExportModule,
  ColumnAutoSizeModule,
  ColumnApiModule,
  RowApiModule,
  RowStyleModule,
  RenderApiModule,
  ClientSideRowModelApiModule,
]);

provideGlobalGridOptions({
  theme: themeQuartz.withParams(agGridThemeParams),
});

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    AvatarColorPipe,
    MatSnackBar,
    Apollo,

    provideAuth({
      config: {
        authority: environment.sso.url,
        postLoginRoute: `/`,
        forbiddenRoute: '/forbidden',
        unauthorizedRoute: '/unauthorized',
        disableIatOffsetValidation: true,
        historyCleanupOff: true,
        redirectUrl: `${window.location.origin}/callback`,
        postLogoutRedirectUri: window.location.origin,
        clientId: environment.sso.clientId,
        responseType: 'code',
        scope: 'profile email permissions organizations openid',
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        silentRenew: true,
        ignoreNonceAfterRefresh: true, // this is required if the id_token is not returned
        triggerRefreshWhenIdTokenExpired: false, // required when refreshing the browser if id_token is not updated after the first authentication
        allowUnsafeReuseRefreshToken: true, // this is required if the refresh token is not rotated
        autoUserInfo: false, // if the user endpoint is not supported
        secureRoutes: [
          environment.graphQLURL,
          environment.apiURL,
          environment.commodityServiceAPIURL,
        ],
        disablePkce: true,
      },
    }),

    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy,
    },
    provideRouter(APP_ROUTES, inMemoryScrollingFeature),
    importProvidersFrom(BrowserModule, MatDialogModule),
    provideAnimations(),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: defaultMatDialogConfig,
    },

    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: GraphqlInterceptorInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: SKIP_ERROR_HANDLING_OPERATIONS_LIST,
      useValue: ['ValidateLoad', 'UpdateLoadFromManager', 'CreateLoads'],
    },
    {
      provide: FILTER_COMPONENT_CONFIG,
      useValue: {
        operatorMap: {
          [SqlOperator.Eq]: 'equals',
          [SqlOperator.Neq]: 'does not equal',
          [SqlOperator.Gt]: 'greater than',
          [SqlOperator.Gte]: 'greater than or equals',
          [SqlOperator.Lt]: 'less than',
          [SqlOperator.Lte]: 'less than or equals',
          [SqlOperator.Like]: 'contains',
          [SqlOperator.NotLike]: 'does not contain',
          [SqlOperator.Ilike]: 'contains',
          [SqlOperator.NotIlike]: 'does not contain',
          [SqlOperator.StartsWith]: 'starts with',
          [SqlOperator.EndWith]: 'ends with',
          [SqlOperator.In]: 'is one of',
          [SqlOperator.NotIn]: 'is not one of',
          [SqlOperator.Between]: 'between',
          [SqlOperator.NotBetween]: 'not between',
          [SqlOperator.IsNull]: 'is not set',
          [SqlOperator.IsNotNull]: 'is set',
        },
        uniqueOperators: [SqlOperator.IsNull, SqlOperator.IsNotNull],
      },
    },
    {
      provide: PAGINATION_CONFIG,
      useValue: {
        paginationKey: 'paginatorInfo',
        currentPage: 'currentPage',
        lastPage: 'lastPage',
        total: 'total',
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always',
        subscriptSizing: 'dynamic',
      },
    },
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);

      return {
        default: {
          cache: new InMemoryCache({
            typePolicies: {
              TableSettingPaginator: {
                keyFields: ['data'],
              },
              TableSetting: {
                keyFields: ['name', 'sort', 'columns', ['name']],
              },
            },
          }),
          link: httpLink.create({
            uri: environment.graphQLURL,
            extractFiles: (body) => extractFiles(body, isExtractableFile),
          }),
          defaultOptions: {
            watchQuery: {
              fetchPolicy: 'no-cache',
              errorPolicy: 'ignore',
            },
            query: {
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
            },
          },
        },
      };
    }),
    DecimalPipe,
  ],
}).catch((err) => console.error(err));
